import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import { useMutation } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import {
  InsightReportConfig,
  InsightsReportType,
  ReportSchedule,
  createReportRequest,
} from 'apis/merchant-gateway/insights';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import validateYupSchema from 'common/utils/validateYupSchema';
import { useInsightSubRoutes } from 'modules/insights-v2/hooks/useInsightsSubRoutes';
import { InsightEntity } from 'modules/insights-v2/types';

import { Summary } from './Summary';
import { DataAggregationLevelFormItem } from './form/DataAggregationFormItem';
import { ReportScheduleFormItem } from './form/ReportScheduleFormItem';
import { InsightsReportTypeFormItem } from './form/ReportTypeFormItem';
import { MetricsFormItem } from './form/metrics/MetricsFormItem';
import { insightsReportFormValidationSchema } from './form/validation';
import { VenuesFormItem } from './form/venues/VenuesFormItem';
import { InsightReportForm } from './types';

type ReportFormProps = {
  venues: Venue[];
  reportConfig?: InsightReportConfig;
  insightEntity: InsightEntity;
};

const ReportFormContainer = styled.div`
  top: 16px;
  display: flex;
  width: 100%;
  max-width: 1200px;
  gap: 40px;
  padding-block: 40px;
  padding-inline: 20px;
  align-items: start;
  margin: 0 auto;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    padding-block-end: 150px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  padding-block-start: 40px;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 70%;
  min-width: auto;
  @media (width < ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
  @media (width > 1200px) {
    flex: 1 1 700px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 16px;
  width: 30%;
  min-width: 300px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;

  @media (width > 1200px) {
    flex: 1 1 380px;
  }
  @media (width< ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

export const ReportForm = ({ venues, reportConfig, insightEntity }: ReportFormProps) => {
  const isMobile = useIsMobile();
  const { formatMessage } = useIntl();
  const {
    reports: { failedLink, successLink },
  } = useInsightSubRoutes();
  const history = useHistory();

  const createReportMutation = useMutation(
    (values: InsightReportForm) =>
      createReportRequest({
        reportType: values.reportType,
        aggregationLevel: values.dataAggregationLevel,
        venueIds: values.venueIds,
        metrics: values.metrics,
        schedules: values.reportSchedule,
        entityType: insightEntity.type,
        entityId: insightEntity.id,
      }),
    {
      onSuccess: () => {
        history.push(successLink(history.location));
      },
      onError: () => {
        history.push(failedLink(history.location));
      },
    },
  );

  const initialValues: Partial<InsightReportForm> = useMemo(() => {
    if (!reportConfig || reportConfig.reports.length === 0) {
      return {};
    }
    return {
      venueIds: insightEntity.type === 'venue' ? [insightEntity.id] : [],
      reportType: reportConfig.reports[0].type,
      dataAggregationLevel: reportConfig.reports[0].supportedAggregations[0],
      metrics: reportConfig.reports[0].metrics.filter(metric => metric.requires.length === 0).map(metric => metric.id),
    };
  }, [reportConfig, insightEntity.type, insightEntity.id]);

  if (!reportConfig || reportConfig.reports.length === 0) {
    return (
      <EmptyContainer>
        <Empty title={formatMessage({ id: 'insights.reports.no-reports' })} />
      </EmptyContainer>
    );
  }

  return (
    <ReportFormContainer>
      <Form<InsightReportForm>
        onSubmit={values => createReportMutation.mutate(values)}
        initialValues={initialValues}
        validate={values =>
          validateYupSchema(insightsReportFormValidationSchema, {
            aggregations: reportConfig.reports.find(report => report.type === values.reportType)?.supportedAggregations,
            type: values.reportType,
          })(values)
        }
      >
        {({ handleSubmit, form, values }) => {
          const supportedReportTypes: InsightsReportType[] = reportConfig.reports.map(report => report.type);
          const report = reportConfig.reports.find(report => report.type === values.reportType);
          const supportedAggregationLevels = report?.supportedAggregations ?? [];
          const supportedMetrics = report?.metrics ?? [];

          const selectedVenues = venues.filter(venue => values.venueIds?.includes(venue.id));
          const isOnlyPickerEnabledVenues =
            selectedVenues.length > 0 && selectedVenues.every(venue => venue.picker_api_enabled);
          const isOnlyWoltPlusVenuesSelected =
            selectedVenues.length > 0 && selectedVenues.every(venue => venue.is_wolt_plus);

          const supportedSchedules: ReportSchedule[] = reportConfig.schedules.map(schedule => schedule.type);

          return (
            <>
              <Content>
                <InsightsReportTypeFormItem
                  supportedReportTypes={supportedReportTypes}
                  onChange={(value: InsightsReportType) => {
                    const report = reportConfig.reports.find(report => report.type === value);
                    const supportedAggregationLevels = report?.supportedAggregations ?? [];
                    form.change('dataAggregationLevel', supportedAggregationLevels[0]);
                    const metrics = report?.metrics
                      .filter(metric => metric.requires.length === 0)
                      .map(metric => metric.id);
                    form.change('metrics', metrics);
                  }}
                />
                {supportedAggregationLevels.length > 0 && (
                  <DataAggregationLevelFormItem supportedAggregationLevels={supportedAggregationLevels} />
                )}
                <VenuesFormItem
                  initialValues={{
                    venueIds: values.venueIds ?? [],
                  }}
                  venues={venues}
                  onUpdate={(venueIds: string[]) => {
                    const metrics = report?.metrics
                      .filter(metric => metric.requires.length === 0)
                      .map(metric => metric.id);
                    form.change('metrics', metrics);
                    form.change('venueIds', venueIds);
                  }}
                  disabled={insightEntity.type === 'venue'}
                />
                {supportedMetrics.length > 0 && (
                  <MetricsFormItem
                    supportedMetrics={supportedMetrics}
                    isOnlyPickerEnabledVenues={isOnlyPickerEnabledVenues}
                    isOnlyWoltPlusVenuesSelected={isOnlyWoltPlusVenuesSelected}
                  />
                )}
                <ReportScheduleFormItem supportedSchedules={supportedSchedules} />
                {!isMobile && (
                  <Button variant="blue" size="large" onClick={handleSubmit}>
                    Create Report
                  </Button>
                )}
              </Content>
              <Sidebar>
                <Summary recipient={reportConfig.recipient} />
              </Sidebar>
              {isMobile && (
                <Button variant="blue" size="large" onClick={handleSubmit}>
                  <FormattedMessage id="insights.reports.action" />
                </Button>
              )}
            </>
          );
        }}
      </Form>
    </ReportFormContainer>
  );
};
